// user-meta-multiselect.component.ts
import { Component, OnInit, OnChanges, SimpleChanges, Input, Output, EventEmitter } from '@angular/core';


interface Option {
  value: string;
  label: string;
  children?: Option[];
}

interface UserMeta {
  authorised_agency_driver_app?: string[];
  authorised_defect_app?: string[];
  erLocations?: string[];
  er_dashboard?: boolean;
  erCanApproveInspection?: boolean;
  accessEarnedRecognition?: boolean;
  [key: string]: any;
}



@Component({
  selector: 'app-user-meta-multiselect',
  templateUrl: './user-meta-multiselect.component.html',
  styleUrls: ['./user-meta-multiselect.component.scss']
})
export class UserMetaMultiselectComponent implements OnInit, OnChanges {
  
  @Input() userMeta: UserMeta;
  @Output() userMetaChange = new EventEmitter<UserMeta>();
  
  // Hard-coded nested options
  options: Option[] = [
    {
      value: 'authorised_agency_driver_app',
      label: 'Agency Onboarding App Access',
      children: [
        { value: 'is_section88_approver', label: 'Can Approve Section 88' },
        { value: 'can_uncancel_drivers', label: 'Can Cancel Do Not Use Drivers' }
      ]
    },
    {
      value: 'authorised_defect_app',
      label: 'Defect App Access',
      children: [
        { value: 'is_supervisor', label: 'Supervisor/Manager' },
        { value: 'is_repairer', label: 'Repairer user' }
        
      ]
    },
    {
      value: 'authorised_inspection_app',
      label: 'Safety Inspection Access',
      children: [
        { value: 'is_qc_approver', label: 'Quality Control Approver Access' },
        { value: 'is_team_leader', label: 'Team Leader Approver Access' },
        { value: 'is_inspection_scheduler_viewer', label: 'Inspection Scheduler Access' },
        { value: 'is_brake_test_uploader', label: 'Brake Test Uploader Access' }
        
      ]
    }
  ];

  selectedValues: string[] = [];
  flattenedOptions: Option[] = [];


  ngOnInit() {
    // Flatten options for the select component
    this.flattenedOptions = this.flattenOptions(this.options);
  }

  ngOnChanges(changes: SimpleChanges) {
    // Detect when userMeta input changes
    if (changes['userMeta'] && changes['userMeta'].currentValue) {
      this.selectedValues = this.getInitialSelections();
      console.log('UserMeta changed, initialized selections:', this.selectedValues);
    }
  }

  getInitialSelections(): string[] {
    const selections: string[] = [];
    
    this.options.forEach(parent => {
      if (parent.children && this.userMeta[parent.value]) {
        const metaArray = this.userMeta[parent.value];
        
        // Check if it's an array
        if (Array.isArray(metaArray)) {
          // If the property exists (even as empty array), select the parent
          selections.push(parent.value);
          
          // Add all child permissions from the array
          parent.children.forEach(child => {
            if (metaArray.includes(child.value)) {
              selections.push(child.value);
            }
          });
        }
      }
    });
    
    return selections;
  }

  flattenOptions(options: Option[]): Option[] {
    let result: Option[] = [];
    
    options.forEach(option => {
      if (option.children && option.children.length > 0) {
        // Add parent as disabled option (header)
        result.push({
          value: option.value,
          label: option.label,
          children: option.children
        });
        
        // Add children with indentation in label
        option.children.forEach(child => {
          result.push({
            value: child.value,
            label: `  ${child.label}`,
            children: undefined
          });
        });
      } else {
        result.push(option);
      }
    });
    
    return result;
  }

  onSelectionChange() {
    // Use setTimeout to ensure change detection runs properly
    setTimeout(() => {
      this.processSelectionChanges();
    }, 0);
  }

  processSelectionChanges() {
    // Rebuild the user_meta structure based on selections
    if (this.userMeta) {
      // First pass: Auto-select parents if children are selected
      this.options.forEach(parent => {
        if (parent.children) {
          const hasSelectedChildren = parent.children.some(child => 
            this.selectedValues.includes(child.value)
          );
          
          // Auto-select parent if any children are selected
          if (hasSelectedChildren && !this.selectedValues.includes(parent.value)) {
            this.selectedValues.push(parent.value);
          }
        }
      });
      
      // Second pass: Process each parent category
      this.options.forEach(parent => {
        const parentSelected = this.selectedValues.includes(parent.value);
        
        if (!parentSelected) {
          // If parent is deselected, remove all children from selectedValues
          if (parent.children) {
            parent.children.forEach(child => {
              const childIndex = this.selectedValues.indexOf(child.value);
              if (childIndex > -1) {
                this.selectedValues.splice(childIndex, 1);
              }
            });
          }
          
          // Remove the property from user_meta entirely
          delete this.userMeta[parent.value];
        } else {
          // Parent is selected, build the array with only child permissions
          const selectedForThisParent: string[] = [];
          
          // Add only selected children (module permissions)
          if (parent.children) {
            parent.children.forEach(child => {
              if (this.selectedValues.includes(child.value)) {
                selectedForThisParent.push(child.value);
              }
            });
          }
          
          // Create the array (even if empty - indicates basic app access)
          this.userMeta[parent.value] = selectedForThisParent;
        }
      });
      
      // Emit only the updated user_meta object
      this.userMetaChange.emit(this.userMeta);
      
      console.log('Updated User Meta:', JSON.stringify(this.userMeta, null, 2));
      console.log('Selected Values:', this.selectedValues);
    }
  }

  isParentOption(option: Option): boolean {
    // Parents are no longer disabled - they can be selected for basic access
    return false;
  }

  // Helper to get readable selection summary
  getSelectionSummary(): string[] {
    const summary: string[] = [];
    
    this.options.forEach(parent => {
      if (parent.children) {
        const selectedInCategory = parent.children.filter(child => 
          this.selectedValues.includes(child.value)
        );
        
        if (selectedInCategory.length > 0) {
          summary.push(`${parent.label}: ${selectedInCategory.map(c => c.label).join(', ')}`);
        }
      }
    });
    
    return summary;
  }
}


// WEBPACK FOOTER //
// /Users/brianyoung/Documents/Development/hfg/datalive-core/gae-web/datalive/client/src/app/shared/components/user-meta-multiselect/user-meta-multiselect.component.ts